import { useEffect, useState } from "react";
import { api } from "./api";

// Season Stats field library (SS-IDs) + derived numbers. Totals are stored; per-game, %, PPS and frequency are calculated here.
export const GROUPS = ["Box score", "Advanced box score", "Synergy shot types", "Synergy play types (offense)", "Synergy play types (defense)"];
export const SYNERGY_GROUPS = new Set(GROUPS.slice(2));
export const LEVELS = ["Prep / HS", "College", "Pro"];

let LIB = null;
let LIB_PROMISE = null;
export const loadSeasonStatsFields = () => {
  if (LIB) return Promise.resolve(LIB);
  if (!LIB_PROMISE) LIB_PROMISE = api.seasonStatsFields().then(d => { LIB = d; return d; }).catch(e => { LIB_PROMISE = null; throw e; });
  return LIB_PROMISE;
};
export const useSeasonStatsFields = () => {
  const [lib, setLib] = useState(LIB);
  useEffect(() => { if (!lib) loadSeasonStatsFields().then(setLib).catch(() => setLib({ fields: [], groups: GROUPS })); }, [lib]);
  return lib;
};
export const fieldsByGroup = (lib) => GROUPS.map(g => ({ group: g, fields: (lib?.fields || []).filter(f => f.group === g && f.status === "active") }));
export const fieldName = (lib, id) => (lib?.fields || []).find(f => f.id === id)?.name || id;

// Records for one player, newest season first. `pick(season)` returns that record (default: most recent).
export const useSeasonStats = (playerId) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = () => api.seasonStats(playerId).then(d => setRecords(d.records || [])).catch(() => setRecords([])).finally(() => setLoading(false));
  useEffect(() => { if (playerId) { setLoading(true); reload(); } }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { records, loading, reload, seasons: records.map(r => r.season), latest: records[0] || null };
};

const g = (s, id) => (s && s[id] != null ? Number(s[id]) : null);
const div = (a, b) => (a != null && b != null && b > 0 ? a / b : null);

// Derived values, all null when the inputs are missing (never 0).
export const derive = (stats) => {
  const s = stats || {};
  const games = g(s, "SS-001");
  const per = (id) => div(g(s, id), games);
  const pct = (m, a) => div(g(s, m), g(s, a));
  // PPS: the entered Synergy points per shot (SS-100..107, includes FTs / and-1s) wins; otherwise makes × points / attempts.
  const calcPps = (m, a, pts) => (g(s, m) != null && g(s, a) > 0 ? (g(s, m) * pts) / g(s, a) : null);
  const total = g(s, "SS-097");
  const shotTypes = [
    { key: "rim", label: "Rim", a: "SS-040", m: "SS-041", pps: "SS-100", pts: 2 },
    { key: "floater", label: "Floater / runner", a: "SS-042", m: "SS-043", pps: "SS-101", pts: 2 },
    { key: "hook", label: "Hook shot", a: "SS-044", m: "SS-045", pps: "SS-102", pts: 2 },
    { key: "cs3_unc", label: "C&S 3: uncontested", a: "SS-046", m: "SS-047", pps: "SS-103", pts: 3 },
    { key: "cs3_con", label: "C&S 3: contested", a: "SS-048", m: "SS-049", pps: "SS-104", pts: 3 },
    { key: "otd3", label: "Off-the-dribble 3", a: "SS-050", m: "SS-051", pps: "SS-105", pts: 3 },
    { key: "mid", label: "Mid-range", a: "SS-052", m: "SS-053", pps: "SS-106", pts: 2 },
    { key: "corner3", label: "Corner 3", a: "SS-054", m: "SS-056", pps: "SS-107", pts: 3 },
  ].map(t => ({ ...t, fga: g(s, t.a), fgm: g(s, t.m), fgPct: pct(t.m, t.a), pps: g(s, t.pps) ?? calcPps(t.m, t.a, t.pts), ppsSource: g(s, t.pps) != null ? "synergy" : "calc", calcPps: calcPps(t.m, t.a, t.pts) }));
  const playTypes = [
    ["PT-01", "PnR ball handler", "SS-060", "SS-061", "SS-120", "SS-121", "SS-122"], ["PT-02", "PnR screener", "SS-062", "SS-063", "SS-123", "SS-124", "SS-125"],
    ["PT-03", "Spot up", "SS-064", "SS-065", "SS-126", "SS-127", "SS-128"], ["PT-04", "Off screens", "SS-066", "SS-067", "SS-129", "SS-130", "SS-131"], ["PT-04", "Handoffs", "SS-078", "SS-079", "SS-147", "SS-148", "SS-149"],
    ["PT-05", "Isolation", "SS-068", "SS-069", "SS-132", "SS-133", "SS-134"], ["PT-06", "Post up", "SS-070", "SS-071", "SS-135", "SS-136", "SS-137"],
    ["PT-07", "Cuts", "SS-072", "SS-073", "SS-138", "SS-139", "SS-140"], ["PT-08", "Transition", "SS-074", "SS-075", "SS-141", "SS-142", "SS-143"],
    ["PT-09", "Off. rebounds / putbacks", "SS-076", "SS-077", "SS-144", "SS-145", "SS-146"],
  ].map(([id, label, poss, ppp, fga, fgm, tov]) => ({ id, label, poss: g(s, poss), ppp: g(s, ppp), fga: g(s, fga), fgm: g(s, fgm), fgPct: pct(fgm, fga), tovPct: g(s, tov), freq: div(g(s, poss), total) }));
  return {
    games, perGame: { min: per("SS-002"), pts: per("SS-003"), fga: per("SS-004"), fgm: per("SS-005"), tpa: per("SS-006"), tpm: per("SS-007"), fta: per("SS-008"), ftm: per("SS-009"),
      oreb: per("SS-010"), dreb: per("SS-011"), reb: games && (g(s, "SS-010") != null || g(s, "SS-011") != null) ? ((g(s, "SS-010") || 0) + (g(s, "SS-011") || 0)) / games : null,
      ast: per("SS-012"), stl: per("SS-013"), blk: per("SS-014"), tov: per("SS-015"), pf: per("SS-016") },
    fgPct: pct("SS-005", "SS-004"), tpPct: pct("SS-007", "SS-006"), ftPct: pct("SS-009", "SS-008"), ftRate: pct("SS-008", "SS-004"),
    shotTypes, playTypes, totalPossessions: total,
  };
};

export const fmt = (v, d = 1) => (v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(d));
export const fmtPct = (v) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);
export const defaultSeasonLabel = () => { const y = new Date().getFullYear(); const m = new Date().getMonth(); const start = m >= 6 ? y : y - 1; return `${start}-${String(start + 1).slice(2)}`; };
